import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TRANSLATIONS } from "@/lib/i18n";
import { APP_TRANSLATIONS } from "@/lib/i18n_app";
import { EXTRA } from "@/lib/i18n_extra";
import { EXTRA2 } from "@/lib/i18n_extra2";
import { EXTRA3 } from "@/lib/i18n_extra3";
import { EXTRA4 } from "@/lib/i18n_extra4";
import { setCountryLang } from "@/lib/countries";
import { GENERATED } from "@/lib/i18n_generated";
import { VERIF_I18N } from "@/lib/i18n_verif";
import { FIX5 } from "@/lib/i18n_fix5";
import { FIX6 } from "@/lib/i18n_fix6";
import { FIX7 } from "@/lib/i18n_fix7";
import { registerCategoryTranslator } from "@/lib/categories";

const MERGED = {};
for (const code of new Set([...Object.keys(TRANSLATIONS), ...Object.keys(APP_TRANSLATIONS), ...Object.keys(EXTRA), ...Object.keys(EXTRA2), ...Object.keys(EXTRA3), ...Object.keys(EXTRA4), ...Object.keys(GENERATED)])) {
  MERGED[code] = { ...(GENERATED[code] || {}), ...(APP_TRANSLATIONS[code] || {}), ...(TRANSLATIONS[code] || {}), ...(EXTRA[code] || {}), ...(EXTRA2[code] || {}), ...(EXTRA3[code] || {}), ...(EXTRA4[code] || {}), ...(VERIF_I18N[code] || {}), ...(FIX5[code] || {}), ...(FIX6[code] || {}), ...(FIX7[code] || {}) };
}

const SUPPORTED = new Set(Object.keys(MERGED));

const MANUAL_KEY = "findo_lang_manual";
const AUTO_KEY = "findo_lang_auto";

// Country → default UI language (falls back to English when unsupported).
const COUNTRY_LANG = {
  FR: "fr", BE: "fr", LU: "fr", MC: "fr", CH: "fr", CA: "en", GB: "en", IE: "en", US: "en", AU: "en", NZ: "en",
  ZA: "en", NG: "en", GH: "en", KE: "en", UG: "en", TZ: "en", ZM: "en", ZW: "en", RW: "fr", BI: "fr",
  CM: "fr", SN: "fr", CI: "fr", ML: "fr", BF: "fr", NE: "fr", TG: "fr", BJ: "fr", GN: "fr", GA: "fr", CG: "fr", CD: "fr",
  CF: "fr", TD: "fr", MG: "fr", DJ: "fr", KM: "fr", HT: "fr", MU: "fr", SC: "fr", MR: "ar",
  DZ: "ar", MA: "ar", TN: "ar", LY: "ar", EG: "ar", SA: "ar", AE: "ar", QA: "ar", KW: "ar", BH: "ar", OM: "ar",
  JO: "ar", LB: "ar", SY: "ar", IQ: "ar", YE: "ar", SD: "ar", PS: "ar",
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es", VE: "es", EC: "es", GT: "es", CU: "es", BO: "es",
  DO: "es", HN: "es", PY: "es", SV: "es", NI: "es", CR: "es", PA: "es", UY: "es", GQ: "es",
  PT: "pt", BR: "pt", AO: "pt", MZ: "pt", CV: "pt", GW: "pt", ST: "pt", TL: "pt",
  DE: "de", AT: "de", LI: "de", IT: "it", SM: "it", VA: "it", NL: "nl", SR: "nl", PL: "pl", RU: "ru", BY: "ru", KZ: "ru",
  KG: "ru", UA: "uk", TR: "tr", CY: "el", GR: "el", IL: "he", CN: "zh", TW: "zh", HK: "zh", MO: "zh", SG: "en",
  JP: "ja", KR: "ko", IN: "hi", ID: "id", TH: "th", VN: "vi", SE: "sv", DK: "da", FI: "fi", NO: "no",
  CZ: "cs", RO: "ro", MD: "ro", HU: "hu", SK: "sk", BG: "bg", HR: "hr", AD: "ca",
};

function browserLang() {
  const l = (navigator.language || "").toLowerCase().split("-")[0];
  return SUPPORTED.has(l) ? l : null;
}

function initialLang() {
  const manual = localStorage.getItem(MANUAL_KEY);
  if (manual && SUPPORTED.has(manual)) return manual;
  const auto = localStorage.getItem(AUTO_KEY);
  if (auto && SUPPORTED.has(auto)) return auto;
  return browserLang() || "en";
}


const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(initialLang);
  const [manual, setManual] = useState(() => !!localStorage.getItem(MANUAL_KEY));
  const [detectedNotice, setDetectedNotice] = useState(false);

  // First visit (or no explicit choice yet): detect the language from the visitor's country.
  useEffect(() => {
    if (localStorage.getItem(MANUAL_KEY)) return;
    import("@/lib/api").then(({ api }) => api("/geo/my-country", { auth: false }))
      .then((r) => {
        if (localStorage.getItem(MANUAL_KEY)) return;
        const l = r?.source === "default" ? (browserLang() || "en") : (COUNTRY_LANG[r?.country] || "en");
        const detected = SUPPORTED.has(l) ? l : "en";
        const first = !localStorage.getItem(AUTO_KEY);
        localStorage.setItem(AUTO_KEY, detected);
        setLangState(detected);
        if (first) setDetectedNotice(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = ["ar", "he"].includes(lang) ? "rtl" : "ltr";
    // Persist on the account (best-effort) for localized emails; auto never overrides a manual choice.
    import("@/lib/api").then(({ api, getToken, getB2CToken }) => {
      const body = { language: lang, manual };
      if (getToken()) api("/auth/me/language", { method: "PUT", body }).catch(() => {});
      if (getB2CToken()) api("/b2c/me/language", { method: "PUT", body, b2c: true }).catch(() => {});
    }).catch(() => {});
  }, [lang, manual]);

  // Explicit user choice: remembered and always wins over detection.
  const setLang = useCallback((l) => {
    if (!SUPPORTED.has(l)) return;
    localStorage.setItem(MANUAL_KEY, l);
    setManual(true);
    setLangState(l);
    setDetectedNotice(false);
  }, []);

  // Apply a language saved on the account without marking it as a new manual choice.
  const applyAccountLang = useCallback((l, isManual) => {
    if (!isManual || !SUPPORTED.has(l)) return;
    localStorage.setItem(MANUAL_KEY, l);
    setManual(true);
    setLangState(l);
  }, []);

  const t = useMemo(() => {
    const dict = MERGED[lang] || MERGED.en || MERGED.fr;
    const enDict = MERGED.en || MERGED.fr;
    // English is the platform fallback: a missing key surfaces English, then key.
    return (key) => dict[key] ?? enDict[key] ?? key;
  }, [lang]);

  // Register the active translator so plain (non-React) helpers in lib/categories
  // stay localized. Done during render so children see it on first paint.
  registerCategoryTranslator(t);
  setCountryLang(lang);
  // Set synchronously so the very first API calls already carry the UI language.
  document.documentElement.lang = lang;

  return (
    <LanguageContext.Provider value={{ lang, setLang, applyAccountLang, t, detectedNotice, dismissDetectedNotice: () => setDetectedNotice(false) }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const c = useContext(LanguageContext);
  if (!c) throw new Error("LanguageProvider missing");
  return c;
}
